import { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { BookingService, Order, OrderService } from 'us-web-services';
import { getLongDateString } from 'us-react-components/dist/utils/date';
import { faListUl } from '@fortawesome/pro-solid-svg-icons/faListUl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button, ButtonSize, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const CARD_CLASSES = 'bg-white border border-neutral-200 rounded p-4 mb-3';

type TypeCode = 'booking' | 'membership' | 'reimbursement';

interface TxType {
  code: TypeCode;
  labelKey: string;
}

const formatCurrency = (amount?: number): string => {
  if (amount == null) return '0.00';
  return `$${Number(amount).toFixed(2)}`;
};

export const Transactions = observer((): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id: pathId } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { stores } = useDependencies();
  const user = stores.user.user;

  const isSitter = !!user?.sitter;
  const userId = user?.id;

  const types = useMemo<TxType[]>(() => {
    const base: TxType[] = [
      { code: 'booking', labelKey: 'pages.transactions.types.booking' },
      { code: 'membership', labelKey: 'pages.transactions.types.membership' },
    ];
    if (!isSitter) {
      base.push({ code: 'reimbursement', labelKey: 'pages.transactions.types.reimbursement' });
    }
    return base;
  }, [isSitter]);

  const initialTypeCode = ((): TypeCode => {
    const requestedType = searchParams.get('type') as TypeCode | null;
    if (requestedType && types.some((option) => option.code === requestedType)) {
      return requestedType;
    }
    return 'booking';
  })();

  const [selectedType, setSelectedType] = useState<TxType>(
    types.find((option) => option.code === initialTypeCode) ?? types[0],
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [transactions, setTransactions] = useState<Order[]>([]);
  const [totalSize, setTotalSize] = useState(0);
  const [page, setPage] = useState(1);
  const [loaded, setLoaded] = useState(true);

  useEffect(() => {
    document.title = 'UrbanSitter - Transactions';
  }, []);

  const loadPage = async (nextPage: number, reset = false): Promise<void> => {
    if (!userId) return;
    setLoaded(false);
    const params: Record<string, unknown> = {
      page: nextPage,
      sort: 'createdAt',
      'createdAt.dir': 'desc',
    };
    if (isSitter) {
      params.payeeId = userId;
      params.statuses = 'success,deposit_failed_hard';
    } else {
      params.userId = userId;
      params.statuses = 'success,charge_failed_hard,deposit_failed_hard';
    }

    if (selectedType.code === 'booking') {
      params.reasons = ['booking'];
    } else if (selectedType.code === 'reimbursement') {
      params.reasons = ['reimbursement'];
    } else {
      params.reasons = ['subscription_setup', 'subscription_installment'];
      if (isSitter) {
        params.userId = userId;
        params.payeeId = null;
      }
    }

    try {
      const resp = (await OrderService.getByFilter({ params })).data;
      const data = resp?.data ?? [];
      setTransactions((prev) => (reset ? data : [...prev, ...data]));
      setTotalSize(resp?.meta?.size ?? 0);
    } catch (err) {
      console.warn('[Transactions] load failed', err);
    } finally {
      setLoaded(true);
    }
  };

  useEffect(() => {
    setTransactions([]);
    setPage(1);
    loadPage(1, true);

  }, [selectedType.code, userId]);

  const onMore = (): void => {
    const next = page + 1;
    setPage(next);
    loadPage(next, false);
  };

  const hasMore = transactions.length < totalSize;

  const idForUrl = pathId ?? String(userId ?? '');
  const exportHref =
    selectedType.code === 'booking'
      ? '/app/childcare-transactions'
      : `/app/user/${idForUrl}/reimbursements`;

  const emptyKey = ((): string => {
    if (selectedType.code === 'booking') {
      return isSitter ? 'pages.transactions.empty.sitterBooking' : 'pages.transactions.empty.parentBooking';
    }
    if (selectedType.code === 'reimbursement') return 'pages.transactions.empty.reimbursement';
    return 'pages.transactions.empty.membership';
  })();

  return (
    <div className='container py-8'>
      <div className='flex flex-wrap items-center gap-3 mb-3'>
        <h1 className='text-2xl font-semibold mb-0'>{t('pages.transactions.heading')}</h1>
        {(selectedType.code === 'booking' || selectedType.code === 'reimbursement') && (
          <Button
            id={
              selectedType.code === 'booking'
                ? 'childcare-transactions'
                : 'reimbursement-export'
            }
            buttonType={ButtonType.primary}
            buttonSize={ButtonSize.small}
            className='text-sm'
            onClick={(): void => {
              window.location.assign(exportHref);
            }}
          >
            {t('pages.transactions.exportFriendly')}
          </Button>
        )}
      </div>

      {!isSitter && (
        <h2 className='text-base mb-4'>{t('pages.transactions.chargesSubheading')}</h2>
      )}

      <div className='relative inline-block mb-4'>
        <Button
          buttonType={ButtonType.tertiary}
          className='flex items-center gap-2 font-semibold !text-xl !text-current'
          onClick={(): void => setMenuOpen((previous) => !previous)}
        >
          <FontAwesomeIcon icon={faListUl} className='text-base' />
          <span>{t(selectedType.labelKey)}</span>
        </Button>
        {menuOpen && (
          <ul className='absolute left-0 mt-2 bg-white border border-neutral-200 rounded shadow z-10 w-56'>
            {types.map((option) => (
              <li key={option.code}>
                <Button
                  buttonType={ButtonType.tertiary}
                  className='w-full text-left !rounded-none px-3 py-2 hover:bg-neutral-100'
                  onClick={(): void => {
                    setSelectedType(option);
                    setMenuOpen(false);
                  }}
                >
                  {t(option.labelKey)}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className='max-w-3xl'>
        {transactions.map((order) => (
          <TransactionCard
            key={order.id}
            transaction={order}
            typeCode={selectedType.code}
            isSitter={isSitter}
            onClick={(): void => {
              if (order.itemBooking?.bookingId) {
                navigate(`/bookings/${order.itemBooking.bookingId}`);
              }
            }}
          />
        ))}

        {transactions.length === 0 && loaded && (
          <h2 className='text-lg'>{t(emptyKey)}</h2>
        )}

        {hasMore && (
          <div className='text-center mt-5'>
            <Button buttonType={ButtonType.primary} onClick={onMore}>
              {loaded ? (
                t('pages.transactions.moreTransactions')
              ) : (
                <span>
                  <span
                    className='inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin align-middle mr-2'
                    aria-hidden='true'
                  />
                  {t('pages.transactions.loading')}
                </span>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
});

interface CardProps {
  transaction: Order;
  typeCode: TypeCode;
  isSitter: boolean;
  onClick: () => void;
}

const TransactionCard = ({ transaction, typeCode, isSitter, onClick }: CardProps): JSX.Element => {
  const { t } = useTranslation();
  const { stores } = useDependencies();
  const [firstName, setFirstName] = useState<string>('');

  useEffect(() => {
    if (
      typeCode !== 'booking' ||
      isSitter ||
      transaction.status !== 'charge_failed_hard' ||
      !transaction.itemBooking?.bookingId
    ) {
      return;
    }
    const load = async (): Promise<void> => {
      try {
        const resp = await BookingService.get(transaction.itemBooking!.bookingId!, {
          params: { userId: stores.user.user?.id },
        });
        const booking = resp.data?.data;
        setFirstName(booking?.caregiver?.firstName ?? '');
      } catch {
        setFirstName('');
      }
    };
    void load();
  }, [transaction, typeCode, isSitter, stores.user.user?.id]);

  const created = getLongDateString(transaction.created) || t('pages.transactions.dateNotAvailable');

  if (typeCode === 'membership') {
    const hasDiscount = Number(transaction.discount ?? 0) > 0;
    return (
      <div className={CARD_CLASSES}>
        <div className='flex gap-3'>
          <span className='icon-sm parent-member' />
          <div>
            <b>
              {t('pages.transactions.totalCharged', {
                amount: `$${transaction.totalIn ?? 0}`,
              })}
            </b>
            <br />
            {created}
            {hasDiscount && (
              <>
                <div>
                  {t('pages.transactions.fee', { amount: `$${transaction.fee ?? 0}` })}
                </div>
                <div>
                  {t('pages.transactions.discountApplied', {
                    amount: `$${transaction.discount}`,
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (typeCode === 'reimbursement') {
    return (
      <div className={CARD_CLASSES}>
        <div className='flex gap-3'>
          <span className='icon-sm parent-member' />
          <div>
            <b>
              {t('pages.transactions.totalReimbursed', {
                amount: `$${transaction.totalOut ?? 0}`,
              })}
            </b>
            <br />
            {created}
          </div>
        </div>
      </div>
    );
  }

  // booking — parent view
  if (!isSitter) {
    const ok =
      transaction.status === 'success' || transaction.status === 'deposit_failed_hard';
    const failed = transaction.status === 'charge_failed_hard';
    const tip = Number(transaction.itemBooking?.tip ?? 0);
    const bookingDiscount = Number(transaction.itemBooking?.discount ?? 0);
    const bookingTotal = transaction.itemBooking?.total;
    const showServiceTotal = tip > 0 || bookingDiscount > 0;
    const platformFee = Number(transaction.platformFee ?? 0);
    const credit = Number(transaction.discount ?? 0);
    return (
      <div
        className={`${CARD_CLASSES} cursor-pointer`}
        onClick={onClick}
        onKeyDown={(event): void => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick();
          }
        }}
        role='button'
        tabIndex={0}
      >
        {ok && <span className='icon-sm icon-svg-checked inline-block mr-2' />}
        {failed && (
          <div className='flex items-center text-red-600'>
            <i className='fa-solid fa-dollar-sign mr-2' />
            <b>{t('pages.transactions.paymentNotProcessed')}</b>
          </div>
        )}
        <div className='ml-8'>
          {ok && (
            <div>
              <b>
                {t('pages.transactions.totalCharged', {
                  amount: formatCurrency(transaction.totalIn),
                })}
              </b>
            </div>
          )}
          <div>{created}</div>
          {showServiceTotal && (
            <div>
              {t('pages.transactions.serviceTotal', {
                amount: formatCurrency(bookingTotal),
              })}
            </div>
          )}
          {tip > 0 && (
            <div>
              {t('pages.transactions.tip', { amount: formatCurrency(tip) })}
            </div>
          )}
          {platformFee > 0 && (
            <div>
              {t('pages.transactions.platformFee', {
                amount: formatCurrency(platformFee),
              })}
            </div>
          )}
          {credit > 0 && (
            <div>
              {t('pages.transactions.creditApplied', {
                amount: formatCurrency(credit),
              })}
            </div>
          )}
        </div>
        {failed && (
          <div>
            <Button
              id='go-bookings'
              buttonType={ButtonType.secondary}
              onClick={(event): void => {
                event.stopPropagation();
                onClick();
              }}
              className='mt-3'
              style={{ width: 200 }}
            >
              {t('pages.transactions.payButton', { name: firstName })}
            </Button>
          </div>
        )}
      </div>
    );
  }

  // booking — sitter view (duplicated in caregiver app; this branch unused in families)
  return <></>;
};
